import { MODULE_KEYS, type Entitlements, type ModuleKey } from '../types/tenant.js';
import {
  TENANT_RESOURCE_KEYS,
  type Group,
  type Level,
  type LevelMap,
  type Role,
  type TenantResource,
} from '../types/rbac.js';
import type { TenantUser } from '../types/user.js';

const LEVEL_ORDER: Record<Level, number> = { none: 0, read: 1, full: 2 };

export function maxLevel(a: Level, b: Level): Level {
  return LEVEL_ORDER[a] >= LEVEL_ORDER[b] ? a : b;
}

export function atLeast(level: Level, required: Level): boolean {
  return LEVEL_ORDER[level] >= LEVEL_ORDER[required];
}

/** A cell click cycles none → read → full → none. */
export function cycleLevel(level: Level): Level {
  switch (level) {
    case 'none':
      return 'read';
    case 'read':
      return 'full';
    case 'full':
      return 'none';
  }
}

export function levelMap(fill: Level): LevelMap {
  return Object.fromEntries(TENANT_RESOURCE_KEYS.map((k) => [k, fill])) as LevelMap;
}

export function withLevels(base: LevelMap, overrides: Partial<LevelMap>): LevelMap {
  return { ...base, ...overrides };
}

/**
 * The one role every tenant starts with. Everything else is created by the organization
 * as modules ship; predefined Operator, Finance and Viewer roles were removed on request.
 */
export const BUILT_IN_ROLE_TEMPLATES: ReadonlyArray<Pick<Role, 'name' | 'description' | 'levels'>> = [
  {
    name: 'Tenant Admin',
    description: 'Manages people, groups and roles, and holds full access to every module the organization has.',
    levels: levelMap('full'),
  },
];

/** Roles that cannot be deleted: the model needs someone besides the superadmins who can manage people. */
export const PROTECTED_ROLE_NAMES: readonly string[] = ['Tenant Admin'];

export function isProtectedRole(role: Pick<Role, 'name' | 'builtIn'>): boolean {
  return role.builtIn && PROTECTED_ROLE_NAMES.includes(role.name);
}

export interface EffectiveLevelsInput {
  user: Pick<TenantUser, 'isSuperadmin' | 'groupIds' | 'status'>;
  groups: readonly Group[];
  roles: readonly Role[];
  entitlements: Entitlements;
}

/**
 * The single computation of what a client user may do. Maximum over the roles of the
 * user's groups, intersected with the tenant's entitlements. Management is always
 * entitled. The superadmin holds full on everything entitled. Suspended and invited users
 * hold nothing.
 */
export function effectiveLevels(input: EffectiveLevelsInput): LevelMap {
  const { user, groups, roles, entitlements } = input;
  if (user.status !== 'Active') return levelMap('none');

  let granted: LevelMap;
  if (user.isSuperadmin) {
    granted = levelMap('full');
  } else {
    granted = levelMap('none');
    const roleIds = new Set(
      groups.filter((g) => user.groupIds.includes(g.id)).flatMap((g) => g.roleIds),
    );
    for (const role of roles) {
      if (!roleIds.has(role.id)) continue;
      for (const key of TENANT_RESOURCE_KEYS) {
        granted[key] = maxLevel(granted[key], role.levels[key]);
      }
    }
  }

  return intersectWithEntitlements(granted, entitlements);
}

/** A grant on an unentitled module grants nothing. */
export function intersectWithEntitlements(levels: LevelMap, entitlements: Entitlements): LevelMap {
  const out = { ...levels };
  for (const key of MODULE_KEYS) {
    if (!entitlements[key]) out[key] = 'none';
  }
  return out;
}

export interface RoleSummary {
  full: number;
  read: number;
  none: number;
  management: Level;
}

/** Counts over the given modules plus the management level, from the live matrix. */
export function roleSummary(role: Pick<Role, 'levels'>, keys: readonly ModuleKey[] = MODULE_KEYS): RoleSummary {
  const summary: RoleSummary = { full: 0, read: 0, none: 0, management: role.levels.management };
  for (const key of keys) summary[role.levels[key]] += 1;
  return summary;
}

/** Users assigned to each role, through their groups. Counted from the user list. */
export function roleAssignmentCounts(
  users: readonly TenantUser[],
  groups: readonly Group[],
  roles: readonly Role[],
): Record<string, number> {
  const counts: Record<string, number> = Object.fromEntries(roles.map((r) => [r.id, 0]));
  for (const user of users) {
    const roleIds = new Set(
      groups.filter((g) => user.groupIds.includes(g.id)).flatMap((g) => g.roleIds),
    );
    for (const id of roleIds) if (id in counts) counts[id] = (counts[id] ?? 0) + 1;
  }
  return counts;
}

/** Users in each group, counted from the user list. */
export function groupMemberCounts(
  users: readonly TenantUser[],
  groups: readonly Group[],
): Record<string, number> {
  const counts: Record<string, number> = Object.fromEntries(groups.map((g) => [g.id, 0]));
  for (const user of users) {
    for (const id of user.groupIds) if (id in counts) counts[id] = (counts[id] ?? 0) + 1;
  }
  return counts;
}

/** Role names are unique within a tenant, case-insensitively. */
export function isRoleNameTaken(name: string, roles: readonly Pick<Role, 'name'>[]): boolean {
  const wanted = name.trim().toLowerCase();
  return wanted.length > 0 && roles.some((r) => r.name.trim().toLowerCase() === wanted);
}

export function isGroupNameTaken(name: string, groups: readonly Pick<Group, 'name'>[]): boolean {
  const wanted = name.trim().toLowerCase();
  return wanted.length > 0 && groups.some((g) => g.name.trim().toLowerCase() === wanted);
}

export function isModuleKey(value: string): value is ModuleKey {
  return (MODULE_KEYS as readonly string[]).includes(value);
}

export function isTenantResource(value: string): value is TenantResource {
  return (TENANT_RESOURCE_KEYS as readonly string[]).includes(value);
}

/**
 * Locked cells protect the role model (spec 08). They apply to built-in roles by name and
 * cannot be changed by click or by API.
 */
export const BUILT_IN_LOCKS: ReadonlyArray<{ roleName: string; resource: TenantResource; level: Level; why: string }> = [
  { roleName: 'Tenant Admin', resource: 'management', level: 'full', why: 'A tenant must always have a role that can manage people.' },
];

/** The locked level for a cell, or null when the cell is free. */
export function lockedLevel(role: Pick<Role, 'name' | 'builtIn'>, resource: TenantResource): Level | null {
  if (!role.builtIn) return null;
  return BUILT_IN_LOCKS.find((l) => l.roleName === role.name && l.resource === resource)?.level ?? null;
}

/** Groups that carry a role. Deleting the role removes it from each of them. */
export function groupsUsingRole(roleId: string, groups: readonly Group[]): Group[] {
  return groups.filter((g) => g.roleIds.includes(roleId));
}

/** People in a group. */
export function membersOf(groupId: string, users: readonly TenantUser[]): TenantUser[] {
  return users.filter((u) => u.groupIds.includes(groupId));
}

/** What a set of groups grants, before entitlements. Used for warnings while inviting. */
export function levelsFromGroups(groupIds: readonly string[], groups: readonly Group[], roles: readonly Role[]): LevelMap {
  const roleIds = new Set(groups.filter((g) => groupIds.includes(g.id)).flatMap((g) => g.roleIds));
  const out = levelMap('none');
  for (const role of roles) {
    if (!roleIds.has(role.id)) continue;
    for (const key of TENANT_RESOURCE_KEYS) out[key] = maxLevel(out[key], role.levels[key]);
  }
  return out;
}

/**
 * The least-privileged group, for the invite default. No management first, then the
 * fewest full-access modules, then the fewest read-only modules; ties broken by name.
 * Null when there are no groups.
 */
export function leastPrivilegedGroup(groups: readonly Group[], roles: readonly Role[]): Group | null {
  const rank = (g: Group): [number, number, number] => {
    const levels = levelsFromGroups([g.id], groups, roles);
    const full = MODULE_KEYS.filter((k) => levels[k] === 'full').length;
    const read = MODULE_KEYS.filter((k) => levels[k] === 'read').length;
    return [LEVEL_ORDER[levels.management], full, read];
  };
  return (
    [...groups].sort((a, b) => {
      const [am, af, ar] = rank(a);
      const [bm, bf, br] = rank(b);
      return am - bm || af - bf || ar - br || a.name.localeCompare(b.name);
    })[0] ?? null
  );
}

export interface AccessSummary {
  full: ModuleKey[];
  read: ModuleKey[];
  hidden: ModuleKey[];
  management: Level;
}

/**
 * Effective access grouped for display. One computation for every "what can they do" view.
 * Screens pass `SHIPPED_MODULES` so modules that do not exist yet never appear.
 */
export function accessSummary(levels: LevelMap, keys: readonly ModuleKey[] = MODULE_KEYS): AccessSummary {
  const full: ModuleKey[] = [];
  const read: ModuleKey[] = [];
  const hidden: ModuleKey[] = [];
  for (const k of keys) {
    if (levels[k] === 'full') full.push(k);
    else if (levels[k] === 'read') read.push(k);
    else hidden.push(k);
  }
  return { full, read, hidden, management: levels.management };
}
